import {Spinner} from "@blueprintjs/core";
import {FilePondFile} from "filepond";
import {FormikProps} from "formik";
import {FGListen, showError, useApiEffect} from "nsitools-react";
import * as React from "react";
import {useHistory} from "react-router";
import * as Yup from "yup";

import {ClubApi, CompetitionRequestApi, CompetitionRequestDto, EPlatform, PersonApi} from "../../../../api";
import {PageContainer} from "../../../../components";
import {useAuth} from "../../../../contexts";
import {useApiService, useTl} from "../../../../hooks";
import {useReferential} from "../../../../hooks/useReferential";
import {ETLCodes} from "../../../../locales";
import {DrupalStepperButtonBloc} from "../../../common";
import {DrupalFormGenerator} from "../../../common/DrupalFormGenerator";
import {DrupalStepper} from "../../../common/DrupalStepper";
import {DrupalFieldSet} from "../../../common/DurpalFieldSet";
import {EDrupalRoutes} from "../../../EDrupalRoutes";
import {DrupalCompetitionClubSelection} from "./steps/DrupalCompetitionClubSelection";
import {DrupalCompetitionClubValidation} from "./steps/DrupalCompetitionClubValidation";
import {DrupalCompetitionDateSelection} from "./steps/DrupalCompetitionDateSelection";
import {useMount} from "ahooks";

export interface IDrupalNewCompetitionRequestProps {
}

export const DrupalNewCompetitionRequest: React.FunctionComponent<IDrupalNewCompetitionRequestProps> = (props) => {
    const personApi = useApiService(PersonApi);
    const clubApi = useApiService(ClubApi);
    const api = useApiService(CompetitionRequestApi);
    const formikRef = React.useRef<FormikProps<CompetitionRequestDto>>();
    const history = useHistory();

    useMount(() => api.checkPortalAuthorization());

    const [currentStep, setcurrentStep] = React.useState(0);
    const [clubIds, clubIdsLoading] = useApiEffect(() => personApi.getLinkedClubs(), []);
    const [clubId, setClubId] = React.useState<number>();
    const [sectionId, setSectionId] = React.useState<number>();
    const [activityId, setActivityId] = React.useState<number>();
    const [nextButtonLoading, setnextButtonLoading] = React.useState<boolean>(false);
    const {user} = useAuth();
    const [sections, sloading] = useReferential((a) => a.getSectionsForClub(clubId), true, [clubId]);
    const [activities, aloading] = useReferential((a) => a.getActivities(sectionId), true, [sectionId]);
    const [subActivities, saloading] = useReferential((a) => a.getSubActivities(activityId), true, [activityId]);
    const [clubs, cloading] = useReferential((a) => a.getClubs(user?.personId ?? 0), true, [user]);
    const [postCodes, ploading] = useReferential((a) => a.getPostalCodes(), true, []);

    const [file, setfile] = React.useState<FilePondFile>();

    const {t} = useTl();
    const data = React.useMemo(() => {
        return CompetitionRequestDto.fromJS({
            clubId: clubIds?.length === 1 ? clubIds[0] : 0,
            request: {personId: null, platform: EPlatform.PORTAL},
            dateTo: null,
            judgeIds: [],
        }).toJSON();
    }, [clubIds]);

    const isClubReadonly = React.useMemo(() => !clubIdsLoading && clubIds?.length === 1, [clubIds, clubIdsLoading]);

    const RequestedFormSchema = React.useMemo(() => {
        return Yup.object().shape({
            clubId: Yup.number()
                .nullable()
                .required(t(ETLCodes.Required))
                .test("is-not-excluded", t(ETLCodes.ClubIsExcluded), async function (value) {
                    if (!value) return true;
                    const result = await clubApi.isClubExcluded(value);
                    return !result.value;
                }),
            dateFrom:
                currentStep > 0
                    ? Yup.date()
                        .nullable()
                        .required(t(ETLCodes.Required))
                        .test("date-test", t(ETLCodes.DateMustBeSmallerThanEndDate), function (value: Date) {
                            if (this.parent.dateTo && value) {
                                return value <= this.parent.dateTo;
                            }
                            return true;
                        })
                    : Yup.date().nullable(),
            dateTo:
                currentStep > 0
                    ? Yup.date()
                        .nullable()
                        .required(t(ETLCodes.Required))
                        .test("date-test", t(ETLCodes.DateMustBeGreaterThanStartDate), function (value: Date) {
                            if (this.parent.dateFrom && value) {
                                return value >= this.parent.dateFrom;
                            }
                            return true;
                        })
                    : Yup.date().nullable(),
            sectionId:
                currentStep > 0
                    ? Yup.number()
                        .nullable()
                        .required(t(ETLCodes.Required))
                        .test("is-part-of-club", t(ETLCodes.SectionIsNotPartOfClubError), async function (value) {
                            if (value && this.parent.clubId) {
                                const result = await api.competitionSectionCheck(this.parent.clubId, value);
                                return result.value;
                            }
                            return true;
                        })
                    : Yup.number().nullable(),
            activityId: currentStep > 0 ? Yup.number().nullable().required(t(ETLCodes.Required)) : Yup.number().nullable(),
            subActivityId: currentStep > 0 ? Yup.number().nullable().required(t(ETLCodes.Required)) : Yup.number().nullable(),
            request: Yup.object().shape({
                personId: Yup.number()
                    .nullable()
                    .required(t(ETLCodes.Required))
                    .test("is-of-club", t(ETLCodes.PersonIsNotOfClub), async function (value) {
                        if (clubId && value) {
                            const res = await api.competitionPersonCheck(clubId, value);
                            return res.value;
                        }
                        return true;
                    }),
            }),
        });
    }, [api, clubApi, clubId, currentStep, t]);

    const checkStep = React.useCallback(
        async (stepNumber: number) => {
            if (stepNumber < currentStep) {
                //don't check when back
                setcurrentStep(stepNumber);
                return;
            }
            if (stepNumber > currentStep + 1) {
                //step number keeps incrementing when form error, this fixes it
                stepNumber = currentStep + 1;
            }
            if (currentStep === 2 && stepNumber === currentStep) {
                //if this is last step, save
                formikRef.current.submitForm();
            } else {
                var errors = Object.keys(formikRef.current.errors); //if there is any error (other than on save)
                var requestErrors = !!formikRef?.current?.errors?.request
                    ? Object.keys(formikRef.current.errors.request)?.map((e) => "request." + e)
                    : [];
                if (!!errors && errors.length && stepNumber > currentStep) {
                    errors = [...errors, ...requestErrors];
                    errors.map((e) => formikRef.current.setFieldTouched(e, true, true));
                    showError(t(ETLCodes.InvalidFormData));
                } else {
                    setcurrentStep(stepNumber);
                }
            }
        },
        [currentStep, t]
    );

    React.useEffect(() => {
        console.log("currentStep", currentStep);
    }, [currentStep]);

    const save = React.useCallback(
        async (data: any) => {
            console.log("got data", data);
            console.log("file", file);
            const formData = data as CompetitionRequestDto;
            setnextButtonLoading(true);
            try {
                const res = await api.save(formData);
                if (!!file) {
                    api.saveFile(res, {data: file.file, fileName: file.filename});
                }
                setnextButtonLoading(false);
                history.push(`${EDrupalRoutes.competitionRequest}/${res}`);
                return res;
            } catch (e) {
                console.log("error", e);
                showError(t(ETLCodes.Error));
                setnextButtonLoading(false);
            }
        },
        [api, file, history, t]
    );

    return (
        <>
            {" "}
            <PageContainer>
                <DrupalFieldSet>
                    {clubIdsLoading ? (
                        <Spinner/>
                    ) : (
                        <div>
                            <DrupalStepper steps={3} stepNumber={currentStep}/>
                            <DrupalFormGenerator
                                initialValues={data}
                                onSubmit={save}
                                hideButtons
                                validationSchema={RequestedFormSchema}
                                formikInnerRef={(f) => (formikRef.current = f)}
                                enableDirtyCheck={false}
                            >
                                <FGListen
                                    field="clubId"
                                    onChanged={async (value, formik) => {
                                        setClubId(value);
                                        const email = await clubApi.getClubEmail(value);
                                        formik.setFieldValue("clubEmail", email);
                                    }}
                                />
                                <FGListen
                                    field="sectionId"
                                    onChanged={(value, formik) => {
                                        setSectionId(value);
                                        formik.setFieldValue("activityId", null);
                                    }}
                                />
                                <FGListen
                                    field="activityId"
                                    onChanged={(value, formik) => {
                                        setActivityId(value);
                                        formik.setFieldValue("subActivityId", null);
                                    }}
                                />
                                {currentStep === 0 ? (
                                    <DrupalCompetitionClubSelection
                                        clubId={clubId}
                                        clubReadOnly={isClubReadonly}
                                        clubs={clubs}
                                        cloading={cloading}
                                    />
                                ) : null}
                                {currentStep === 1 ? (
                                    <DrupalCompetitionDateSelection
                                        activities={activities}
                                        aloading={aloading}
                                        postCodes={postCodes}
                                        ploading={ploading}
                                        subActivities={subActivities}
                                        saloading={saloading}
                                        sections={sections}
                                        sloading={sloading}
                                        formik={formikRef?.current}
                                        onFileUpload={(f) => setfile(f)}
                                    />
                                ) : null}

                                {currentStep === 2 ? (
                                    <DrupalCompetitionClubValidation
                                        activities={activities}
                                        aloading={aloading}
                                        subActivities={subActivities}
                                        saloading={saloading}
                                        sections={sections}
                                        sloading={sloading}
                                        clubId={clubId}
                                        clubs={clubs}
                                        cloading={cloading}
                                    />
                                ) : null}

                                <DrupalStepperButtonBloc
                                    onStepChange={async (step) => {
                                        await formikRef.current.validateForm();

                                        checkStep(step);
                                    }}
                                    backOnly={false}
                                    forceStepperBack={true}
                                    nextButtonIcon={currentStep === 2 ? "floppy-disk" : "chevron-right"}
                                    nextButtonText={currentStep === 2 ? t(ETLCodes.General_Save) : t(ETLCodes.Next)}
                                    loading={nextButtonLoading}
                                />
                            </DrupalFormGenerator>
                        </div>
                    )}
                </DrupalFieldSet>
            </PageContainer>
        </>
    );
};
